import { BoxGeometry, ConeGeometry, CylinderGeometry, IcosahedronGeometry, type BufferGeometry } from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

function merge(parts: BufferGeometry[]): BufferGeometry {
  const merged = mergeGeometries(parts.map((p) => (p.index ? p.toNonIndexed() : p)));
  if (!merged) throw new Error('could not merge prop geometry');
  return merged;
}

/** Big shade tree (neem / mahogany style): trunk + lumpy canopy. */
export function canopyTreeParts(): { trunk: BufferGeometry; canopy: BufferGeometry } {
  const trunk = new CylinderGeometry(0.22, 0.35, 4, 7).translate(0, 2, 0);
  const blobs = [
    new IcosahedronGeometry(2.6, 1).scale(1.2, 0.75, 1.2).translate(0, 5.2, 0),
    new IcosahedronGeometry(1.9, 1).scale(1, 0.8, 1).translate(1.6, 4.8, 0.6),
    new IcosahedronGeometry(1.8, 1).scale(1, 0.8, 1).translate(-1.4, 5.0, -0.8),
  ];
  return { trunk, canopy: merge(blobs) };
}

/** Royal palm, like the avenues in Aburi and on Accra medians: tall grey trunk, crown of fronds. */
export function palmParts(): { trunk: BufferGeometry; fronds: BufferGeometry } {
  const trunk = merge([
    new CylinderGeometry(0.28, 0.4, 9, 8).translate(0, 4.5, 0),
    new CylinderGeometry(0.34, 0.28, 1.4, 8).translate(0, 9.6, 0),
  ]);
  const fronds: BufferGeometry[] = [];
  for (let i = 0; i < 9; i += 1) {
    const angle = (i / 9) * Math.PI * 2;
    const leaf = new BoxGeometry(0.5, 0.06, 3.4).translate(0, 0, 1.7);
    leaf.rotateX(0.35 + (i % 3) * 0.15);
    leaf.rotateY(angle);
    leaf.translate(0, 10.3, 0);
    fronds.push(leaf);
  }
  return { trunk, fronds: merge(fronds) };
}

/** Where the two lamp heads sit on a street light, relative to its base. */
export const LAMP_HEADS = [{ x: 2.2, y: 8.8 }, { x: -2.2, y: 8.8 }] as const;

/** Double-arm street light, as on Liberation Road's median: the pole and arm, and the two lamp heads (which glow at night). */
export function streetlightParts(): { pole: BufferGeometry; heads: BufferGeometry } {
  return {
    pole: merge([new CylinderGeometry(0.1, 0.16, 9, 6).translate(0, 4.5, 0), new BoxGeometry(4.4, 0.12, 0.12).translate(0, 8.9, 0)]),
    heads: merge(LAMP_HEADS.map((head) => new BoxGeometry(0.8, 0.18, 0.35).translate(head.x, head.y, 0))),
  };
}

/** Market umbrella over a table-top stall. */
export function umbrellaParts(): { canopy: BufferGeometry; base: BufferGeometry } {
  const canopy = new ConeGeometry(1.4, 0.7, 8).translate(0, 2.35, 0);
  const base = merge([
    new CylinderGeometry(0.04, 0.04, 2.3, 5).translate(0, 1.15, 0),
    new BoxGeometry(1.2, 0.08, 0.8).translate(0, 0.8, 0),
    new BoxGeometry(1.0, 0.25, 0.6).translate(0, 0.95, 0),
  ]);
  return { canopy, base };
}

/** Black polytank water tank, on nearly every roof in Accra. */
export function polytankGeometry(): BufferGeometry {
  return merge([new CylinderGeometry(0.75, 0.75, 1.6, 12).translate(0, 0.8, 0), new CylinderGeometry(0.3, 0.3, 0.2, 8).translate(0, 1.7, 0)]);
}
